import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { SessionActualInput, SessionEvaluationInput } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useRecordActuals,
  useSaveEvaluation,
  useSessionRegister,
  type RegisterRow,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { sheetName } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { SheetNumber } from '@/ui/Sheet';

const STATUSES = ['present', 'late', 'modified', 'injured', 'excused', 'absent'] as const;
const FLAG_KINDS = ['tired', 'sore', 'off', 'pain'] as const;

type Attendance = SessionActualInput['attendance'][number];

/**
 * A stored evaluation comes back with nulls where nothing was written; the form
 * wants those absent, so an untouched field stays untouched rather than being
 * rendered as the string "null".
 */
function stripNulls(row: Record<string, unknown>): SessionEvaluationInput {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (v !== null && v !== undefined && k !== 'sessionId' && k !== 'orgId') out[k] = v;
  }
  return out as SessionEvaluationInput;
}

/**
 * The pitch-side screen.
 *
 * Designed for a phone held in one hand with the other on a whistle: everyone
 * starts present, the rating is ten large targets, and flagging a player is one
 * tap. The brief allows two to three minutes after a session; this is built for
 * under one.
 */
export function Run({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { sessionId } = useParams<{ sessionId: string }>();
  const register = useSessionRegister(sessionId ?? null);
  const record = useRecordActuals(sessionId ?? '');
  const evaluate = useSaveEvaluation(sessionId ?? '');
  const { busy, formError, submit } = useAuthForm();

  const [rows, setRows] = useState<Map<string, Attendance>>(new Map());
  const [flags, setFlags] = useState<Map<string, string>>(new Map());
  const [rpe, setRpe] = useState<number | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [evaluation, setEvaluation] = useState<SessionEvaluationInput>({});

  const players: RegisterRow[] = register.data?.players ?? [];
  const session = register.data?.session;
  const saved = register.data?.evaluation ?? null;

  /**
   * Show what is already recorded before letting anyone write over it.
   *
   * A session can be rated more than once — a player turns up late, an
   * attendance mark was wrong — and this screen saves wholesale. Opening it
   * blank and saving used to replace the coach's rating with the planned
   * estimate and wipe the evaluation, because what was sent was nothing and
   * nothing was treated as a value. Everything the save writes is loaded here
   * first, which is the only version of this that is safe.
   */
  useEffect(() => {
    if (players.length === 0 || rows.size > 0) return;
    setRows(
      new Map(
        players.map((p) => [
          p.playerId,
          {
            playerId: p.playerId,
            status: (p.status as Attendance['status']) ?? 'present',
            minutesDone: p.minutesDone ?? undefined,
          },
        ]),
      ),
    );
    // What was rated before, falling back to what was planned only when this
    // session has never been rated at all.
    if (minutes === null) {
      setMinutes(session?.actualDurationMin ?? session?.plannedDurationMin ?? null);
    }
    if (rpe === null && session?.actualRpe != null) setRpe(session.actualRpe);
    if (saved) setEvaluation(stripNulls(saved));
  }, [players, rows.size, minutes, rpe, session, saved]);

  const setStatus = (playerId: string, status: Attendance['status']) =>
    setRows((m) => {
      const next = new Map(m);
      const existing = next.get(playerId) ?? { playerId, status: 'present' as const };
      next.set(playerId, { ...existing, status });
      return next;
    });

  const setMinutesFor = (playerId: string, value: number | undefined) =>
    setRows((m) => {
      const next = new Map(m);
      const existing = next.get(playerId) ?? { playerId, status: 'present' as const };
      next.set(playerId, { ...existing, minutesDone: value });
      return next;
    });

  const toggleFlag = (playerId: string, kind: string) =>
    setFlags((m) => {
      const next = new Map(m);
      if (next.get(playerId) === kind) next.delete(playerId);
      else next.set(playerId, kind);
      return next;
    });

  if (register.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (register.isError || !session) {
    return (
      <>
        <PageHeader
          title={t('run.title')}
          lead={t('run.lead')}
          help={localePath(locale, '/guide/run')}
        />
        <Panel title={t('run.title')}>
          <EmptyState title={t('run.noSession')} body={t('run.noSessionBody')} />
        </Panel>
      </>
    );
  }

  const trained = [...rows.values()].filter((r) =>
    ['present', 'late', 'modified'].includes(r.status),
  ).length;

  return (
    <>
      <PageHeader
        title={session.title || session.objective || t('run.title')}
        lead={t('run.lead')}
        help={localePath(locale, '/guide/run')}
      />

      {formError ? (
        <div className="mb-4">
          <Notice tone="risk">{formError}</Notice>
        </div>
      ) : null}
      {savedCount !== null ? (
        <div className="mb-4">
          <Notice tone="ok">{t('run.saved', { count: savedCount })}</Notice>
        </div>
      ) : null}

      <Panel title={t('sessions.run')}>
        <Section title={t('run.rating')} hint={t('run.ratingHint')} explain="rpe">
          {/* Ten large targets: this is tapped outdoors, in gloves, at speed. */}
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={rpe === n}
                onClick={() => setRpe(n)}
                className={`tabular size-12 rounded-(--radius-control) border font-condensed text-subhead font-semibold transition-colors ${
                  rpe === n
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="mt-4 w-40">
            <Field label={t('run.duration')}>
              {(p) => (
                <TextInput
                  {...p}
                  type="number"
                  min={0}
                  max={300}
                  value={minutes ?? ''}
                  onChange={(e) =>
                    setMinutes(e.target.value === '' ? null : Number(e.target.value))
                  }
                />
              )}
            </Field>
          </div>
        </Section>

        <Section title={t('run.register')}>
          <ul className="divide-y divide-line">
            {players.map((p) => {
              const row = rows.get(p.playerId);
              const status = row?.status ?? 'present';
              const flagged = flags.get(p.playerId);
              return (
                <li key={p.playerId} className="py-2.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <SheetNumber>{p.shirtNumber ?? '–'}</SheetNumber>
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {sheetName(p.firstName, p.lastName)}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          aria-pressed={status === s}
                          onClick={() => setStatus(p.playerId, s)}
                          className={`min-h-11 min-w-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                            status === s
                              ? 'border-ink bg-ink text-paper'
                              : 'border-line bg-surface text-ink-faint hover:border-ink hover:text-ink'
                          }`}
                        >
                          {t(`run.${s}`)}
                        </button>
                      ))}
                    </div>
                    {status === 'modified' || status === 'late' ? (
                      <TextInput
                        type="number"
                        min={0}
                        max={300}
                        aria-label={t('run.minutes')}
                        placeholder={t('run.minutes')}
                        className="w-24 !text-meta"
                        value={row?.minutesDone ?? ''}
                        onChange={(e) =>
                          setMinutesFor(
                            p.playerId,
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    ) : null}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1 ps-11">
                    {FLAG_KINDS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        aria-pressed={flagged === k}
                        onClick={() => toggleFlag(p.playerId, k)}
                        className={`min-h-11 min-w-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                          flagged === k
                            ? 'border-warn bg-warn/10 text-warn'
                            : 'border-line text-ink-faint hover:border-warn hover:text-warn'
                        }`}
                      >
                        {t(`run.flag_${k}`)}
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-meta text-ink-faint">{t('run.flagsHint')}</p>
        </Section>

        <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
          <Button
            variant="primary"
            busy={busy}
            disabled={rpe === null}
            onClick={async () => {
              const ok = await submit(async () => {
                const result = await record.mutateAsync({
                  actualRpe: rpe ?? undefined,
                  actualDurationMin: minutes ?? undefined,
                  actualPlayers: trained,
                  attendance: [...rows.values()],
                  flags: [...flags.entries()].map(([playerId, kind]) => ({
                    playerId,
                    kind: kind as 'tired' | 'sore' | 'off' | 'pain' | 'other',
                  })),
                });
                setSavedCount(result.players);
              });
              if (!ok) setSavedCount(null);
            }}
          >
            {t('run.saveActuals')}
          </Button>
          <span className="tabular text-meta text-ink-faint">
            {trained} / {players.length}
          </span>
        </div>
      </Panel>

      <div className="mt-5">
        <Panel title={t('run.evaluation')}>
          <Section title={t('run.objectiveAchieved')}>
            <div className="flex flex-wrap gap-1.5">
              {(['yes', 'partial', 'no'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={evaluation.objectiveAchieved === v}
                  onClick={() => setEvaluation((e) => ({ ...e, objectiveAchieved: v }))}
                  className={`min-h-11 rounded-(--radius-control) border px-4 transition-colors ${
                    evaluation.objectiveAchieved === v
                      ? 'border-ink bg-ink text-paper'
                      : 'border-line bg-surface text-ink-muted hover:border-ink'
                  }`}
                >
                  {t(`run.achieved_${v}`)}
                </button>
              ))}
            </div>

            <div className="mt-4 grid gap-x-5 gap-y-2 lg:grid-cols-2">
              {(
                [
                  ['why', 'why'],
                  ['whatWorked', 'whatWorked'],
                  ['whatDidNot', 'whatDidNot'],
                  ['playerResponse', 'playerResponse'],
                  ['unexpectedIssue', 'unexpectedIssue'],
                  ['repeatNext', 'repeatNext'],
                  ['changeNext', 'changeNext'],
                ] as const
              ).map(([field, key]) => (
                <Field key={field} label={t(`run.${key}`)} optional>
                  {(p) => (
                    <TextArea
                      {...p}
                      value={(evaluation[field] as string | undefined) ?? ''}
                      onChange={(e) =>
                        setEvaluation((ev) => ({ ...ev, [field]: e.target.value || undefined }))
                      }
                    />
                  )}
                </Field>
              ))}
            </div>
          </Section>

          <div className="border-t border-line px-4 py-4">
            <Button
              variant="secondary"
              onClick={() => void evaluate.mutateAsync(evaluation)}
              busy={evaluate.isPending}
            >
              {t('run.saveEvaluation')}
            </Button>
          </div>
        </Panel>
      </div>
    </>
  );
}
